"use client";

import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import {
  type PersistentDebugLogger,
  usePersistentDebugLogger,
} from "./use-persistent-debug-logger";
import { IS_SEQ_LOG_ENABLED } from "./use-seq-log";

const BATCHED_SEQ_MODULE_EVAL_ID = Date.now().toString(36);

export enum BatchedSeqFlushReason {
  WindowElapsed = "window-elapsed",
  SessionStopped = "session-stopped",
  Manual = "manual",
}

interface BatchedSeqPayloadInput<TWindow> {
  window: TWindow;
  runId: string;
  windowStartedAtMs: number;
  windowEndedAtMs: number;
  elapsedMs: number;
  flushReason: BatchedSeqFlushReason;
}

interface UseBatchedSeqDiagnosticsInput<TWindow> {
  component: string;
  instanceId: string;
  eventName: string;
  windowMs: number;
  metadata?: Record<string, unknown>;
  createWindow: () => TWindow;
  buildPayload: (
    input: BatchedSeqPayloadInput<TWindow>,
  ) => Record<string, unknown>;
}

interface FlushBatchedSeqDiagnosticsInput {
  nowMs: number;
  reason?: BatchedSeqFlushReason;
  force?: boolean;
}

export interface BatchedSeqDiagnostics<TWindow> {
  runId: string;
  logger: PersistentDebugLogger;
  getWindow: () => TWindow;
  resetWindow: (startedAtMs: number) => void;
  flushWindow: (input: FlushBatchedSeqDiagnosticsInput) => boolean;
  flushWindowIfDue: (nowMs: number) => boolean;
}

interface BatchedWindowState<TWindow> {
  startedAtMs: number;
  value: TWindow;
}

export function useBatchedSeqDiagnostics<TWindow>({
  component,
  instanceId,
  eventName,
  windowMs,
  metadata,
  createWindow,
  buildPayload,
}: UseBatchedSeqDiagnosticsInput<TWindow>): BatchedSeqDiagnostics<TWindow> {
  const reactRunId = useId();
  const runId = `${BATCHED_SEQ_MODULE_EVAL_ID}-${reactRunId}`;
  const logger = usePersistentDebugLogger({
    component,
    instanceId,
    metadata: { ...metadata, runId },
  });
  const createWindowRef = useRef(createWindow);
  const buildPayloadRef = useRef(buildPayload);
  const [initialWindow] = useState(createWindow);
  const windowRef = useRef<BatchedWindowState<TWindow>>({
    startedAtMs: 0,
    value: initialWindow,
  });

  useLayoutEffect(() => {
    createWindowRef.current = createWindow;
    buildPayloadRef.current = buildPayload;
  });

  const getWindow = useCallback((): TWindow => windowRef.current.value, []);

  const resetWindow = useCallback(
    (startedAtMs: number): void => {
      try {
        windowRef.current = {
          startedAtMs,
          value: createWindowRef.current(),
        };
      } catch (error) {
        windowRef.current.startedAtMs = startedAtMs;
        try {
          logger.logError({
            name: `${eventName}-batch-reset-failed`,
            error,
            payload: { runId },
          });
        } catch {
          // Diagnostics must never alter the measured runtime path.
        }
      }
    },
    [eventName, logger, runId],
  );

  const flushWindow = useCallback(
    ({
      nowMs,
      reason = BatchedSeqFlushReason.Manual,
      force = false,
    }: FlushBatchedSeqDiagnosticsInput): boolean => {
      if (!IS_SEQ_LOG_ENABLED) return false;

      const currentWindow = windowRef.current;
      const elapsedMs = Math.max(0, nowMs - currentWindow.startedAtMs);
      if (!force && elapsedMs < windowMs) return false;

      try {
        windowRef.current = {
          startedAtMs: nowMs,
          value: createWindowRef.current(),
        };
        logger.logMarker({
          name: eventName,
          payload: {
            runId,
            windowStartedAtMs: currentWindow.startedAtMs,
            windowEndedAtMs: nowMs,
            elapsedMs,
            flushReason: reason,
            ...buildPayloadRef.current({
              window: currentWindow.value,
              runId,
              windowStartedAtMs: currentWindow.startedAtMs,
              windowEndedAtMs: nowMs,
              elapsedMs,
              flushReason: reason,
            }),
          },
        });
        return true;
      } catch (error) {
        windowRef.current.startedAtMs = nowMs;
        try {
          logger.logError({
            name: `${eventName}-batch-build-failed`,
            error,
            payload: { runId, elapsedMs, flushReason: reason },
          });
        } catch {
          // Diagnostics must never alter the measured runtime path.
        }
        return false;
      }
    },
    [eventName, logger, runId, windowMs],
  );

  const flushWindowIfDue = useCallback(
    (nowMs: number): boolean =>
      flushWindow({
        nowMs,
        reason: BatchedSeqFlushReason.WindowElapsed,
      }),
    [flushWindow],
  );

  return {
    runId,
    logger,
    getWindow,
    resetWindow,
    flushWindow,
    flushWindowIfDue,
  };
}
